import { useState } from 'react';
import {
  Card, CardBody, Badge, Button, Nav, NavItem, NavLink, TabContent, TabPane,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Row, Col,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useValisightCandidates, useValisightExplained,
  useAutoExplain, useManualExplain, useDismissCandidate,
} from './useValisight';

const SEV_COLORS = { info: 'secondary', warn: 'warning', error: 'danger', critical: 'danger' };
const SEV_OPTIONS = ['info', 'warn', 'error', 'critical'];
const ORIGIN_LABEL = { pending: 'offen', errordb: 'errorDB', llm: 'KI', manual: 'manuell' };

function SevBadge({ severity }) {
  return <Badge color={SEV_COLORS[severity] ?? 'secondary'} pill>{severity}</Badge>;
}

const EMPTY = { fingerprint: '', title: '', severity: 'error', explanationMd: '', causeMd: '', solutionMd: '', articleUrl: '' };

// Manueller Erklärungs-Editor. Vorbefüllt aus Kandidat oder bestehendem Eintrag.
function ManualEditor({ open, initial, onClose, canWrite }) {
  const [form, setForm] = useState(initial ?? EMPTY);
  const save = useManualExplain();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    await save.mutateAsync({
      fingerprint: form.fingerprint,
      title: form.title.trim() || undefined,
      severity: form.severity,
      explanationMd: form.explanationMd.trim() || undefined,
      causeMd: form.causeMd.trim() || undefined,
      solutionMd: form.solutionMd.trim() || undefined,
      articleUrl: form.articleUrl.trim() || undefined,
    });
    onClose();
  };

  const valid = form.title.trim() || form.explanationMd.trim();

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" scrollable>
      <ModalHeader toggle={onClose}>Erklärung schreiben</ModalHeader>
      <ModalBody>
        {form.sample && (
          <pre className="bg-light border rounded p-2 small text-truncate" title={form.sample}>{form.sample}</pre>
        )}
        <Row>
          <Col md={8}>
            <FormGroup>
              <Label>Titel</Label>
              <Input value={form.title} onChange={set('title')} placeholder="z. B. Datenbankverbindung abgelehnt" />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Schweregrad</Label>
              <Input type="select" value={form.severity} onChange={set('severity')}>
                {SEV_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label>Was bedeutet das? (Markdown)</Label>
          <Input type="textarea" rows={4} value={form.explanationMd} onChange={set('explanationMd')} />
        </FormGroup>
        <FormGroup>
          <Label>Wahrscheinliche Ursache (optional)</Label>
          <Input type="textarea" rows={3} value={form.causeMd} onChange={set('causeMd')} />
        </FormGroup>
        <FormGroup>
          <Label>Lösung (optional)</Label>
          <Input type="textarea" rows={3} value={form.solutionMd} onChange={set('solutionMd')} />
        </FormGroup>
        <FormGroup>
          <Label>Weiterführender Artikel (URL, optional)</Label>
          <Input value={form.articleUrl} onChange={set('articleUrl')} placeholder="https://…" />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={onClose}>Abbrechen</Button>
        <Button color="primary" disabled={!valid || !canWrite || save.isPending} onClick={submit}>
          {save.isPending ? 'Speichere …' : 'Speichern'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function CandidatesTab({ canWrite }) {
  const query = useValisightCandidates();
  const auto = useAutoExplain();
  const dismiss = useDismissCandidate();
  const [editor, setEditor] = useState(null);
  const items = query.data?.pending ?? [];

  const writeManual = (c) => setEditor({
    initial: { ...EMPTY, fingerprint: c.fingerprint, severity: c.severity ?? 'error', title: (c.title || c.sample || '').slice(0, 80), sample: c.sample },
  });

  return (
    <div>
      <p className="text-muted small">
        Häufige, noch <strong>nicht erklärte</strong> Log-Fingerprints (nur maskierte Beispielzeilen).
        „Auto-Erklären“ versucht errorDB→LLM; „Schreiben“ öffnet den Editor; „Verwerfen“ stoppt weitere Auto-Versuche.
      </p>
      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && items.length === 0}
        emptyMessage="Keine offenen Kandidaten. 🎉"
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 70 }} className="text-end">Anzahl</th>
            <th style={{ width: 80 }}>Severity</th>
            <th>Beispiel-Zeile</th>
            <th style={{ width: 120 }}>Zuletzt</th>
            <th style={{ width: 260 }} className="text-end">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.fingerprint}>
              <td className="text-end"><Badge color="dark" pill>{c.seenCount}</Badge></td>
              <td><SevBadge severity={c.severity} /></td>
              <td className="small font-monospace text-truncate" style={{ maxWidth: 420 }} title={c.sample}>{c.sample}</td>
              <td><RelativeTime iso={c.lastSeenAt} /></td>
              <td className="text-end">
                {canWrite && (
                  <>
                    <Button size="sm" color="primary" outline disabled={auto.isPending}
                      onClick={() => auto.mutate({ fingerprint: c.fingerprint })}>Auto-Erklären</Button>{' '}
                    <Button size="sm" color="secondary" outline onClick={() => writeManual(c)}>Schreiben</Button>{' '}
                    <Button size="sm" color="link" className="text-muted" disabled={dismiss.isPending}
                      onClick={() => dismiss.mutate({ fingerprint: c.fingerprint })}>Verwerfen</Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {editor && <ManualEditor open initial={editor.initial} canWrite={canWrite} onClose={() => setEditor(null)} />}
    </div>
  );
}

function ExplainedTab({ canWrite }) {
  const [q, setQ] = useState('');
  const query = useValisightExplained(q);
  const [editor, setEditor] = useState(null);
  const items = query.data?.items ?? [];

  const edit = (e) => setEditor({
    initial: {
      ...EMPTY, fingerprint: e.fingerprint, title: e.title ?? '', severity: e.severity ?? 'error',
      explanationMd: e.explanationMd ?? '', causeMd: e.causeMd ?? '', solutionMd: e.solutionMd ?? '',
      articleUrl: e.articleUrl ?? '', sample: e.sample,
    },
  });

  return (
    <div>
      <div className="mb-3" style={{ maxWidth: 320 }}>
        <Input bsSize="sm" placeholder="Suchen (Titel/Beispiel) …" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && items.length === 0}
        emptyMessage="Noch keine erklärten Einträge."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 80 }}>Severity</th>
            <th>Titel</th>
            <th style={{ width: 90 }}>Quelle</th>
            <th style={{ width: 70 }} className="text-end">Anzahl</th>
            <th style={{ width: 120 }} className="text-end">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.fingerprint}>
              <td><SevBadge severity={e.severity} /></td>
              <td>
                {e.title || <span className="text-muted">— ohne Titel —</span>}
                <div className="small text-muted font-monospace text-truncate" style={{ maxWidth: 380 }} title={e.sample}>{e.sample}</div>
              </td>
              <td><Badge color={e.origin === 'manual' ? 'success' : 'info'} pill>{ORIGIN_LABEL[e.origin] ?? e.origin}</Badge></td>
              <td className="text-end small text-muted">{e.seenCount}</td>
              <td className="text-end">
                {canWrite && <Button size="sm" color="link" onClick={() => edit(e)}>Bearbeiten</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {editor && <ManualEditor open initial={editor.initial} canWrite={canWrite} onClose={() => setEditor(null)} />}
    </div>
  );
}

export function ValisightCurationPage() {
  const [tab, setTab] = useState('candidates');
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  return (
    <div>
      <PageHeader
        title="ValiSight — Log-Kuration"
        subtitle="Kandidaten-Inbox (unerklärte Fingerprints) + Erklärungen schreiben/korrigieren"
      />
      <Card>
        <CardBody>
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink active={tab === 'candidates'} onClick={() => setTab('candidates')} style={{ cursor: 'pointer' }}>Kandidaten-Inbox</NavLink>
            </NavItem>
            <NavItem>
              <NavLink active={tab === 'explained'} onClick={() => setTab('explained')} style={{ cursor: 'pointer' }}>Erklärt</NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={tab}>
            <TabPane tabId="candidates">{tab === 'candidates' && <CandidatesTab canWrite={canWrite} />}</TabPane>
            <TabPane tabId="explained">{tab === 'explained' && <ExplainedTab canWrite={canWrite} />}</TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
}
